import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Empty, Space, Table, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvHireService, { formatHireMoney } from '@/services/pmv-hire.service';
import { HIRE_STATUS_COLOR } from '@/utils/pmvHireUi';

const { Text } = Typography;

/**
 * Hire Control Tower — commercial control KPIs.
 * Missing rate / incomplete MTD must never display as 0.
 */
const PmvHireControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-hire-control-tower'],
    queryFn: () => pmvHireService.getControlTower(),
    staleTime: 30_000,
  });
  const diagnosticsQ = useQuery({
    queryKey: ['pmv-hire-diagnostics'],
    queryFn: () => pmvHireService.getDiagnostics(),
    staleTime: 60_000,
  });

  const d = q.data;
  const by = d?.by_commercial_status ?? {};
  const offHirePending =
    d?.off_hire_pending ?? (by.OFF_HIRE_REQUESTED ?? 0) + (by.OFF_HIRE_EFFECTIVE ?? 0);
  const mtdComplete = d?.hire_cost_mtd_complete === true;
  const mtdLabel = mtdComplete ? 'Hire cost MTD (Final)' : 'Hire cost MTD (Estimated / Accrued)';
  const mtdValue = mtdComplete
    ? d?.hire_cost_mtd_final
    : d?.hire_cost_mtd_accrued ?? d?.hire_cost_mtd_estimated;

  const statusRows = useMemo(
    () =>
      Object.entries(by)
        .map(([status, count]) => ({ status, count: Number(count) || 0 }))
        .sort((a, b) => b.count - a.count),
    [by]
  );

  const diagEntries = useMemo(() => {
    const raw = diagnosticsQ.data;
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw)
      .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
      .slice(0, 12)
      .map(([k, v]) => ({ key: k, value: String(v) }));
  }, [diagnosticsQ.data]);

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Hire Control"
        subtitle="Commercial interpretation of verified ops — Procurement / AP / Finance remain authorities"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/asset/pmv/hire/agreements/new')}>
              New agreement
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire')}>On-Hire register</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/agreements')}>Agreements</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/on-off')}>On / Off-Hire</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/timesheets')}>Timesheets</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/statements')}>Statements</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/reconciliations')}>
              Invoice recon
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/attention')}>Attention</Button>
          </Space>
        }
      />

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Authority boundary"
        description={
          d?.note ||
          'PMV Hire never posts AP/GL. Working hours ≠ billable hours. Missing rate is Unavailable, never 0.'
        }
      />

      {!q.isLoading && (d?.open_attention ?? 0) === 0 && (d?.open_reconciliations ?? 0) === 0 ? (
        <Empty style={{ marginBottom: 16 }} description="No hire commercial attention right now." />
      ) : null}

      <ControlTowerSectionCard zone="attention" title="Requires attention">
        <ControlTowerMetricStrip zone="attention" title="Attention">
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Units on hire"
              count={d?.on_hire_count ?? 0}
              footerLink={{
                label: 'On-hire register',
                onClick: () => navigate('/asset/pmv/hire?status=ON_HIRE'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Open commercial attention"
              count={d?.open_attention ?? 0}
              urgency={(d?.open_attention ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Open queue',
                onClick: () => navigate('/asset/pmv/hire/attention'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Invoice variance / open recon"
              count={d?.invoice_variance_count ?? d?.open_reconciliations ?? 0}
              urgency={(d?.open_reconciliations ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Reconciliations',
                onClick: () => navigate('/asset/pmv/hire/reconciliations'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Off-hire pending"
              count={offHirePending}
              urgency={offHirePending > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Lifecycle',
                onClick: () => navigate('/asset/pmv/hire/on-off'),
              }}
            />
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="metricsOrders" title="Commercial pipeline">
        <ControlTowerMetricStrip zone="metricsOrders" title="Pipeline">
          <Col xs={12} sm={8} md={6}>
            <div style={{ padding: 12 }}>
              <Text type="secondary">{mtdLabel}</Text>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{formatHireMoney(mtdValue)}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Incomplete periods stay Estimated/Accrued — never fake Final as 0
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Unverified logs (proxy)"
              count={d?.unverified_logs ?? d?.stale_calculations ?? 0}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Timesheets pending"
              count={d?.timesheets_pending ?? 0}
              footerLink={{
                label: 'Timesheets',
                onClick: () => navigate('/asset/pmv/hire/timesheets'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile title="Stale calculations" count={d?.stale_calculations ?? 0} />
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="metricsOrders" title="Fleet by commercial status">
        {statusRows.length === 0 ? (
          <Empty description="No status breakdown yet" />
        ) : (
          <Table
            size="small"
            rowKey="status"
            pagination={false}
            dataSource={statusRows}
            columns={[
              {
                title: 'Status',
                dataIndex: 'status',
                render: (s: string) => (
                  <Tag color={HIRE_STATUS_COLOR[s] || 'default'}>{s}</Tag>
                ),
              },
              { title: 'Count', dataIndex: 'count', width: 100 },
              {
                title: '',
                width: 120,
                render: (_: unknown, r: { status: string }) => (
                  <Button
                    type="link"
                    onClick={() => navigate(`/asset/pmv/hire?status=${r.status}`)}
                  >
                    Open register
                  </Button>
                ),
              },
            ]}
          />
        )}
      </ControlTowerSectionCard>

      {diagEntries.length > 0 ? (
        <ControlTowerSectionCard zone="metricsOrders" title="Diagnostics snapshot">
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={diagEntries}
            columns={[
              { title: 'Signal', dataIndex: 'key' },
              { title: 'Value', dataIndex: 'value', width: 200 },
            ]}
          />
        </ControlTowerSectionCard>
      ) : null}
    </WorkspaceLayout>
  );
};

export default PmvHireControlTowerPage;
